import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { QUERY_GET_UNIT_GENERIC_DETAILS } from '@apps-common/apis/portal/unit'
import {
  ModifyUnitActivationStep,
  stepDirection
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/Index'
import { ImageComponent } from '@apps-common/components/common'
import { ButtonCopy, ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { AccountReservationInputSM, CheckboxInputSM, FieldErrorMessage } from '@apps-common/components/elements/form'
import { CollapsibleContent, VertSpacerLg } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { INFO_ITEM_SERVICE_REQUESTS, InfoModalButton } from '@apps-common/components/modals/info-modal'
import { PortalUnitSelectModal } from '@apps-common/components/modals/PortalUnitSelectModal'
import { commonIcons } from '@apps-common/config/icons'
import { PORTAL_UNIT_FILER_DEFAULTS_INACTIVE } from '@apps-common/config/unit-filter-defaults'
import { UnitPlanUpgradeability } from '@apps-common/config/unit-plan'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IActivationServiceRequestCreateModel } from '@apps-common/types/portal/service-request'
import { IPortalUnitSearchModel, IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'
import { ROUTE_UNIT_ACTIVATION_DETAILS } from '@apps-radionet-portal-root/src/config/private-routes'

interface IProps {
  stepNo: number
  loading: boolean
  updating: boolean
  step: ModifyUnitActivationStep
  moveStep: (direction: stepDirection) => void
  defaultUnit?: IUnitGenericDetailsModel
  hideLineSupervision?: boolean
}

export const UnitActivationUnitForm = ({ stepNo, loading, updating, step, moveStep, defaultUnit, hideLineSupervision }: IProps) => {
  const { navigateNewTab } = useNavigation()
  const { register, setValue, watch, formState } = useFormContext<IActivationServiceRequestCreateModel>()
  const { errors } = formState

  const [previousMcentreId, setPreviousMcentreId] = useState<string>('')

  const mCentreIdVal = watch('mCentreId')
  const unitIdVal = watch('unitId')
  const serialNoVal = watch('serialNo')
  const imeiVal = watch('imei')
  const productImageUrlVal = watch('productImageUrl')
  const productNameVal = watch('productName')
  const cidAccountNoVal = watch('accountNo')

  const updateValues = (
    unitId: string,
    serialNo: string,
    imei: string,
    productImageUrl: string,
    productId: string,
    productName: string,
    planId: string,
    planName: string,
    planUpgradePath: string
  ) => {
    setValue('unitId', unitId, { shouldValidate: true })
    setValue('serialNo', serialNo, { shouldValidate: true })
    setValue('imei', imei, { shouldValidate: true })
    setValue('productImageUrl', productImageUrl, { shouldValidate: true })
    setValue('productId', productId, { shouldValidate: true })
    setValue('productName', productName, { shouldValidate: true })
    setValue('unitPlanUpgradePath', planUpgradePath, { shouldValidate: true })

    if (planUpgradePath === UnitPlanUpgradeability.fixed) {
      setValue('unitPlanId', planId, { shouldValidate: true })
      setValue('unitPlanName', planName, { shouldValidate: true })
    }
  }

  const clearValues = () => {
    setValue('unitId', '')
    setValue('serialNo', '')
    setValue('imei', '')
    setValue('productImageUrl', '')
    setValue('productId', '')
    setValue('unitPlanId', '')
    setValue('unitPlanName', '')
    setValue('unitPlanUpgradePath', '')
    queryClient.resetQueries({ queryKey: QUERY_GET_UNIT_GENERIC_DETAILS(unitIdVal) })
  }

  useEffect(() => {
    if (defaultUnit) {
      const { unitId, serialNo, imei, imageUrl, productId, product, planId, planName, planUpgradePath } = defaultUnit

      updateValues(unitId, serialNo, imei, imageUrl, productId, product, planId, planName, planUpgradePath)
    }
  }, [defaultUnit])

  useEffect(() => {
    register('unitId', {
      validate: (value) => {
        if (step !== ModifyUnitActivationStep.unit) {
          return true
        }

        if (!value || value.length === 0) {
          return 'Inactive device required'
        }

        return true
      }
    })
  })

  useEffect(() => {
    if (!defaultUnit && previousMcentreId && previousMcentreId.length !== 0) {
      clearValues()
    }

    setPreviousMcentreId(mCentreIdVal)
  }, [mCentreIdVal])

  return (
    <CollapsibleContent
      title={`Step ${stepNo} | Device`}
      defaultOpen={step === ModifyUnitActivationStep.unit}
      disabled={step !== ModifyUnitActivationStep.unit}
      classes='bg-surface border border-border rounded-2xl text-text'
    >
      <div className='grid sm:grid-cols-2 gap-[20px] sm:h-[200px]'>
        <div className='flex flex-col items-start justify-center gap-[10px] h-[100%]'>
          <div className='relative grow w-full bg-surface-2 border border-border rounded-2xl flex items-center justify-center p-4'>
            <InfoModalButton
              className='!absolute !top-[10px] !right-[10px] z-[99]'
              iconClasses='text-[1.1rem]'
              subtitle={INFO_ITEM_SERVICE_REQUESTS.inactiveUnit.subtitle}
              title={INFO_ITEM_SERVICE_REQUESTS.inactiveUnit.title}
              body={INFO_ITEM_SERVICE_REQUESTS.inactiveUnit.body}
            />
            {(!unitIdVal || unitIdVal?.length === 0) && (
              <ModalButton
                component={PortalUnitSelectModal}
                componentProps={{
                  title: 'Inactive devices',
                  replacementButtonComponent: (
                    props: IPortalUnitSearchModel,
                    onDismiss: (data?: string | null | undefined | number, role?: string) => void
                  ) => {
                    if (props.unitActivationRequestID) {
                      return (
                        <button
                          type='button'
                          aria-label='Manage device'
                          onClick={(e) => {
                            e?.stopPropagation()
                            navigateNewTab({
                              routeUrl: ROUTE_UNIT_ACTIVATION_DETAILS.buildPath(props.unitActivationRequestID),
                              route: ROUTE_UNIT_ACTIVATION_DETAILS
                            })
                          }}
                        >
                          <div className='flex items-center justify-start rounded-full px-4 py-1 bg-warning text-on-warning'>
                            <div className='jost-medium italic text-[0.85rem] leading-[1rem] text-left rounded-xl py-1'>
                              Device associated with another activation. Delete or archive this activation first.
                            </div>
                            <div className='flex items-center justify-center w-[35px] min-w-[35px] h-[35px] min-h-[35px] rounded-full bg-warning'>
                              <commonIcons.srActivations className='text-[1.5rem]' />
                            </div>
                          </div>
                        </button>
                      )
                    }

                    return undefined
                  },
                  filterDefaults: PORTAL_UNIT_FILER_DEFAULTS_INACTIVE
                }}
                confirmCallback={(data: IPortalUnitSearchModel) => {
                  updateValues(
                    data.unitId,
                    data.serialNo,
                    data.imei,
                    data.productImageUrl,
                    data.productId,
                    data.productName,
                    data.planId,
                    data.planName,
                    data.planUpgradePath
                  )
                }}
                classes='relative w-full pb-7 sm:py-4 p-3'
                enterAnimation={modalSlideUpEnterAnimation}
                leaveAnimation={modalSlideUpLeaveAnimation}
              >
                <div className='relative flex flex-col items-center justify-between gap-[10px]'>
                  <div className='flex flex-col items-center justify-center gap-[2px] bg-surface border border-border rounded-xl px-[15px] py-[6px] jost-light text-[0.9rem] leading-[1.1rem] '>
                    <commonIcons.add className='text-[1.1rem]' />
                    Add
                  </div>
                  <p className='jost-light text-[0.9rem] leading-[1.1rem]'>
                    <div className='jost'>No inactive device added.</div>
                    Select a new inactive device.
                  </p>
                </div>
                <FieldErrorMessage
                  classes='absolute left-0 bottom-[5px] sm:bottom-[-5px] !pl-0 w-full text-center'
                  hideWhenNotInError
                  errors={errors?.unitId}
                />
              </ModalButton>
            )}
            {unitIdVal && unitIdVal?.length > 0 && (
              <div className='w-full bg-surface border border-border rounded-3xl p-4 flex flex-col gap-[15px]'>
                <div className='flex items-start justify-between'>
                  <div className='flex items-center justify-start gap-[5px]'>
                    <div className='w-[50px] h-[50px] rounded-[25px] p-[5px] bg-surface-2 flex items-center justify-center'>
                      <ImageComponent src={productImageUrlVal} alt='Product' imageClasses='max-w-[80%]' errorIconClasses='text-[1.5rem]' />
                    </div>
                    <div className='text-[0.85em] jost-light'>
                      <div className='pl-[3px] uppercase'>{productNameVal}</div>
                    </div>
                  </div>
                  <ButtonRoundSm type='button' className='bg-surface-2' callback={() => clearValues()}>
                    <commonIcons.remove />
                  </ButtonRoundSm>
                </div>
                <div className='flex flex-col gap-[6px] text-[0.8em] leading-[1.2em] px-2'>
                  <div className='flex'>
                    <span className='bg-surface-2 text-text px-1 rounded-[5px] mr-[2px]'>SerialNo</span>
                    <span className='jost-medium'>
                      <ButtonCopy value={serialNoVal} showValue iconClassName='text-[1.1em]' />
                    </span>
                  </div>
                  <div className='flex'>
                    <span className='bg-surface-2 text-text px-1 rounded-[5px] mr-[2px]'>IMEI</span>
                    <span className='jost-medium'>
                      <ButtonCopy value={imeiVal} showValue iconClassName='text-[1.1em]' />
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='border border-border w-full sm:hidden' />
        <div className='flex flex-col gap-[10px] h-[100%]'>
          <div className='grow bg-surface-2 rounded-2xl flex flex-col items-start justify-center'>
            <div className='w-full px-3'>
              <AccountReservationInputSM
                id='accountNo'
                placeholder='Enter CID account'
                label='Account'
                showLabel
                containerClassName='grow sm:grow-none sm:min-w-[220px] pt-[15px]'
                loading={loading}
                disabled={updating}
                existingAccountNo={cidAccountNoVal}
                registration={register('accountNo', {
                  validate: (value) => {
                    if (step !== ModifyUnitActivationStep.unit) {
                      return true
                    }

                    if (!value || value.length === 0) {
                      return 'CID account required'
                    }

                    return true
                  }
                })}
                mCentreId={mCentreIdVal}
                onError={() => {
                  setValue('reservedAccountId', NaN, { shouldValidate: true })
                  setValue('accountNo', '', { shouldValidate: true })
                }}
                onSuccess={(data) => {
                  setValue('reservedAccountId', data.id, { shouldValidate: true })
                  setValue('accountNo', data.accountNo, { shouldValidate: true })
                }}
                onReset={() => {
                  setValue('reservedAccountId', NaN, { shouldValidate: true })
                  setValue('accountNo', '', { shouldValidate: true })
                }}
                errors={errors?.accountNo}
                info='Set contact ID account # assigned to device'
              />
              {!hideLineSupervision && (
                <CheckboxInputSM
                  label='Line supervision'
                  id='useLineSupervision'
                  loading={loading}
                  disabled={updating}
                  registration={register('useLineSupervision')}
                  errors={errors.useLineSupervision}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='flex items-center justify-end gap-[10px] mt-6'>
        {stepNo > 1 && (
          <ButtonRoundedSm
            type='button'
            loading={loading || updating}
            text='Previous'
            className='sm:grow-0 action-neutral'
            icon={<commonIcons.arrowAngledLeft />}
            callback={() => moveStep('backward')}
          />
        )}
        <ButtonRoundedSm
          type='submit'
          loading={loading || updating}
          text='Next'
          iconRight
          className='sm:grow-0 action-success'
          icon={<commonIcons.arrowRight />}
        />
      </div>
      <VertSpacerLg />
    </CollapsibleContent>
  )
}
